import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Home, Pencil, Save, Trash2, Upload, Image,
  Eraser, Undo, Download, Plus, X, Lightbulb, AlertCircle,
  CheckCircle, Palette
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEARNING_GROUPS = [
  { value: 'ks1', label: 'KS1' },
  { value: 'ks2', label: 'KS2' },
  { value: '11-plus', label: '11+' },
  { value: 'ks3', label: 'KS3' },
  { value: 'gcse', label: 'GCSE' },
  { value: 'a-level', label: 'A-Level' },
];

const SUBJECTS = [
  { value: 'maths', label: 'Maths' },
  { value: 'english', label: 'English' },
  { value: 'science', label: 'Science' },
  { value: 'verbal-reasoning', label: 'Verbal Reasoning' },
  { value: 'non-verbal-reasoning', label: 'Non-Verbal Reasoning' },
];

const TIERS = [
  { value: 'core', label: 'Core', color: 'bg-green-500' },
  { value: 'stretch', label: 'Stretch', color: 'bg-amber-500' },
  { value: 'elite', label: 'Elite', color: 'bg-purple-500' },
];

const COLORS = ['#000000', '#ef4444', '#3b82f6', '#22c55e', '#a855f7', '#f97316'];
const BRUSH_SIZES = [2, 4, 8, 12, 16];

const CreateChallenge = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [context, setContext] = useState(null);
  const [brushColor, setBrushColor] = useState('#000000');
  const [brushSize, setBrushSize] = useState(4);
  const [isEraser, setIsEraser] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [canvasHistory, setCanvasHistory] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    subject: 'maths',
    learning_group: '11-plus',
    tier: 'stretch',
    skill_tags: [],
    question_text: '',
    correct_answer: '',
    answer_type: 'text',
    options: ['', '', '', ''],
    hints: ['', '', ''],
    common_misconceptions: [''],
  });
  
  const [newTag, setNewTag] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    if (!token || user.userType !== 'tutor') {
      navigate('/');
      return;
    }
    
    // Initialize canvas
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = canvas.offsetWidth;
      canvas.height = 400;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      setContext(ctx);
    }
  }, [token, user.userType, navigate]);

  // Canvas drawing functions
  const startDrawing = (e) => {
    if (!context) return;
    setIsDrawing(true);
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    context.beginPath();
    context.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing || !context) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    context.strokeStyle = isEraser ? 'white' : brushColor;
    context.lineWidth = isEraser ? brushSize * 3 : brushSize;
    context.lineTo(x, y);
    context.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      context.closePath();
      setIsDrawing(false);
      // Save to history
      const canvas = canvasRef.current;
      setCanvasHistory(prev => [...prev, canvas.toDataURL()]);
    }
  };

  const clearCanvas = () => {
    if (!context || !canvasRef.current) return;
    context.fillStyle = 'white';
    context.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setUploadedImage(null);
  };

  const undoLast = () => {
    if (canvasHistory.length > 1) {
      const newHistory = canvasHistory.slice(0, -1);
      setCanvasHistory(newHistory);
      const img = new Image();
      img.onload = () => {
        context.drawImage(img, 0, 0);
      };
      img.src = newHistory[newHistory.length - 1];
    } else {
      clearCanvas();
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          if (context && canvasRef.current) {
            clearCanvas();
            const canvas = canvasRef.current;
            const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
            const x = (canvas.width - img.width * scale) / 2;
            const y = (canvas.height - img.height * scale) / 2;
            context.drawImage(img, x, y, img.width * scale, img.height * scale);
            setUploadedImage(event.target.result);
          }
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const addTag = () => {
    if (newTag.trim() && !formData.skill_tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        skill_tags: [...prev.skill_tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      skill_tags: prev.skill_tags.filter(t => t !== tag)
    }));
  };

  const updateHint = (index, value) => {
    const newHints = [...formData.hints];
    newHints[index] = value;
    setFormData(prev => ({ ...prev, hints: newHints }));
  };

  const updateOption = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const updateMisconception = (index, value) => {
    const newMisconceptions = [...formData.common_misconceptions];
    newMisconceptions[index] = value;
    setFormData(prev => ({ ...prev, common_misconceptions: newMisconceptions }));
  };

  const addMisconception = () => {
    setFormData(prev => ({
      ...prev,
      common_misconceptions: [...prev.common_misconceptions, '']
    }));
  };

  const handleSubmit = async () => {
    setError('');
    setSubmitting(true);
    
    // Validation
    if (!formData.title.trim()) {
      setError('Please enter a title');
      setSubmitting(false);
      return;
    }
    if (!formData.question_text.trim()) {
      setError('Please enter the question text');
      setSubmitting(false);
      return;
    }
    if (!formData.correct_answer.trim()) {
      setError('Please enter the correct answer');
      setSubmitting(false);
      return;
    }

    try {
      // Get canvas as base64
      const whiteboardImage = canvasRef.current?.toDataURL('image/png');
      
      const payload = {
        ...formData,
        whiteboard_image: whiteboardImage,
        hints: formData.hints.filter(h => h.trim()),
        common_misconceptions: formData.common_misconceptions.filter(m => m.trim()),
        options: formData.answer_type === 'multiple-choice' 
          ? formData.options.filter(o => o.trim()) 
          : null,
      };

      const response = await fetch(`${API}/whiteboard/challenges`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create challenge');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/whiteboard-x');
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (user.userType !== 'tutor') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Card className="bg-slate-800 border-slate-700 p-8 text-center">
          <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-slate-400 mb-4">Only approved tutors can create challenges.</p>
          <Button onClick={() => navigate('/')} className="bg-purple-600">
            Go Home
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 py-6 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/whiteboard-x')} 
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')} 
              className="text-white hover:bg-white/20"
            >
              <Home className="mr-2 h-4 w-4" /> Home
            </Button>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/10 rounded-xl">
              <Pencil className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">Create Challenge</h1>
              <p className="text-purple-200">Design a stretch problem for students</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        {success ? (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-center py-20"
          >
            <CheckCircle className="h-20 w-20 text-green-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Challenge Created!</h2>
            <p className="text-slate-400">Redirecting to challenges...</p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Whiteboard Canvas */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Pencil className="h-5 w-5 text-purple-400" />
                  Whiteboard
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Draw equations, diagrams, or upload an image
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Canvas Tools */}
                <div className="flex flex-wrap items-center gap-2 pb-4 border-b border-slate-700">
                  {/* Colors */}
                  <div className="flex gap-1">
                    {COLORS.map(color => (
                      <button
                        key={color}
                        onClick={() => { setBrushColor(color); setIsEraser(false); }}
                        className={`w-6 h-6 rounded-full border-2 ${brushColor === color && !isEraser ? 'border-white' : 'border-transparent'}`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                  
                  <div className="h-6 w-px bg-slate-600 mx-2" />
                  
                  {/* Brush sizes */}
                  <div className="flex gap-1">
                    {BRUSH_SIZES.map(size => (
                      <button
                        key={size}
                        onClick={() => setBrushSize(size)}
                        className={`w-8 h-8 rounded flex items-center justify-center ${brushSize === size ? 'bg-slate-600' : 'bg-slate-700'}`}
                      >
                        <div 
                          className="rounded-full bg-white"
                          style={{ width: size, height: size }}
                        />
                      </button>
                    ))}
                  </div>
                  
                  <div className="h-6 w-px bg-slate-600 mx-2" />
                  
                  {/* Tools */}
                  <Button
                    size="sm"
                    variant={isEraser ? 'default' : 'outline'}
                    className={isEraser ? 'bg-purple-600' : 'border-slate-600'}
                    onClick={() => setIsEraser(!isEraser)}
                  >
                    <Eraser className="h-4 w-4" />
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-slate-600"
                    onClick={undoLast}
                  >
                    <Undo className="h-4 w-4" />
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-slate-600"
                    onClick={clearCanvas}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <Button size="sm" variant="outline" className="border-slate-600" asChild>
                      <span><Upload className="h-4 w-4 mr-1" /> Upload</span>
                    </Button>
                  </label>
                </div>
                
                {/* Canvas */}
                <div className="border border-slate-600 rounded-lg overflow-hidden">
                  <canvas
                    ref={canvasRef}
                    className="w-full bg-white cursor-crosshair"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Challenge Form */}
            <div className="space-y-6">
              {/* Basic Info */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Challenge Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-slate-400 text-sm block mb-2">Title</label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Day 12 - Modular Arithmetic"
                      className="bg-slate-900/50 border-slate-600 text-white"
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-slate-400 text-sm block mb-2">Subject</label>
                      <select
                        value={formData.subject}
                        onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                        className="w-full bg-slate-900/50 border border-slate-600 rounded-md px-3 py-2 text-white"
                      >
                        {SUBJECTS.map(s => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="text-slate-400 text-sm block mb-2">Level</label>
                      <select
                        value={formData.learning_group}
                        onChange={(e) => setFormData(prev => ({ ...prev, learning_group: e.target.value }))}
                        className="w-full bg-slate-900/50 border border-slate-600 rounded-md px-3 py-2 text-white"
                      >
                        {LEARNING_GROUPS.map(g => (
                          <option key={g.value} value={g.value}>{g.label}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="text-slate-400 text-sm block mb-2">Tier</label>
                      <select
                        value={formData.tier}
                        onChange={(e) => setFormData(prev => ({ ...prev, tier: e.target.value }))}
                        className="w-full bg-slate-900/50 border border-slate-600 rounded-md px-3 py-2 text-white"
                      >
                        {TIERS.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Skill Tags */}
                  <div>
                    <label className="text-slate-400 text-sm block mb-2">Skill Tags</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {formData.skill_tags.map(tag => (
                        <Badge key={tag} className="bg-purple-500/20 text-purple-300">
                          {tag}
                          <button onClick={() => removeTag(tag)} className="ml-2">
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addTag()}
                        placeholder="e.g., ratio reasoning"
                        className="bg-slate-900/50 border-slate-600 text-white flex-1"
                      />
                      <Button onClick={addTag} variant="outline" className="border-slate-600">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Question & Answer */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Question & Answer</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-slate-400 text-sm block mb-2">Question Text</label>
                    <Textarea
                      value={formData.question_text}
                      onChange={(e) => setFormData(prev => ({ ...prev, question_text: e.target.value }))}
                      placeholder="Write the challenge question..."
                      className="bg-slate-900/50 border-slate-600 text-white min-h-[100px]"
                    />
                  </div>
                  
                  <div>
                    <label className="text-slate-400 text-sm block mb-2">Answer Type</label>
                    <div className="flex gap-2">
                      {['text', 'number', 'multiple-choice'].map(type => (
                        <Button
                          key={type}
                          size="sm"
                          variant={formData.answer_type === type ? 'default' : 'outline'}
                          className={formData.answer_type === type ? 'bg-purple-600' : 'border-slate-600'}
                          onClick={() => setFormData(prev => ({ ...prev, answer_type: type }))}
                        >
                          {type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')}
                        </Button>
                      ))}
                    </div>
                  </div>
                  
                  {formData.answer_type === 'multiple-choice' && (
                    <div className="space-y-2">
                      <label className="text-slate-400 text-sm">Options</label>
                      {formData.options.map((opt, idx) => (
                        <div key={idx} className="flex gap-2 items-center">
                          <span className="text-slate-400 w-6">{String.fromCharCode(65 + idx)}.</span>
                          <Input
                            value={opt}
                            onChange={(e) => updateOption(idx, e.target.value)}
                            placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                            className="bg-slate-900/50 border-slate-600 text-white flex-1"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div>
                    <label className="text-slate-400 text-sm block mb-2">Correct Answer</label>
                    <Input
                      value={formData.correct_answer}
                      onChange={(e) => setFormData(prev => ({ ...prev, correct_answer: e.target.value }))}
                      placeholder="Enter the correct answer"
                      className="bg-slate-900/50 border-slate-600 text-white"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Hints */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Lightbulb className="h-5 w-5 text-amber-400" />
                    Hints (for each attempt)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {formData.hints.map((hint, idx) => (
                    <div key={idx}>
                      <label className="text-slate-400 text-sm block mb-1">Hint {idx + 1}</label>
                      <Textarea
                        value={hint}
                        onChange={(e) => updateHint(idx, e.target.value)}
                        placeholder={`Hint after attempt ${idx + 1}...`}
                        className="bg-slate-900/50 border-slate-600 text-white"
                        rows={2}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Common Misconceptions */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    Common Misconceptions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {formData.common_misconceptions.map((misc, idx) => (
                    <Textarea
                      key={idx}
                      value={misc}
                      onChange={(e) => updateMisconception(idx, e.target.value)}
                      placeholder="Describe a common mistake students make..."
                      className="bg-slate-900/50 border-slate-600 text-white"
                      rows={2}
                    />
                  ))}
                  <Button 
                    variant="outline" 
                    className="border-slate-600 text-slate-400"
                    onClick={addMisconception}
                  >
                    <Plus className="h-4 w-4 mr-2" /> Add Misconception
                  </Button>
                </CardContent>
              </Card>

              {/* Error Display */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
                  {error}
                </div>
              )}

              {/* Submit */}
              <Button 
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 py-6 text-lg"
              >
                {submitting ? (
                  <>Creating Challenge...</>
                ) : (
                  <><Save className="h-5 w-5 mr-2" /> Publish Challenge</>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateChallenge;
